import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import "../styles/DetalheAluno.css";
import { supabase } from "../services/supabase";
import aulasService from "../services/aulas.service"; // Importar serviço de aulas
import { formatarData as formatarDataUtil } from "../lib/utils"; // Corrigido: caminho para lib/utils.js
import { useAuth } from "../hooks/useAuth";
import { getViewAs } from "../lib/viewAs";

// Iniciais para o avatar (ex.: "Marco Fernandes" -> "MF")
const iniciais = (nome = "") =>
  nome
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase() || "?";

// Regra §2: aula "realizada" = a AULA foi realizada/finalizada E este aluno
// esteve PRESENTE nela. O status da aula sozinho não basta: a aula é
// finalizada normalmente mesmo quando o aluno faltou (fix 27/07 — o histórico
// mostrava "REALIZADA" no dia da falta e o contador somava falta como aula).
const ehRealizada = (aula) =>
  (aula?.status === "realizada" || aula?.status === "finalizada") &&
  aula?.status_aluno === "presente";

// Função auxiliar para interpretar horários de início/fim com base no status da aula
const interpretarHorarios = (aula) => {
  const hora = aula.hora;

  // Se não temos hora registrada (NULL ou undefined), retornar valores padrão
  if (!hora) {
    switch (aula.status) {
      case "realizada":
      case "finalizada":
        return {
          horaInicio: "Não registrado",
          horaFim: "Não registrado",
        };
      case "cancelada":
        return {
          horaInicio: "Cancelada",
          horaFim: "Cancelada",
        };
      default:
        return {
          horaInicio: "--:--",
          horaFim: "--:--",
        };
    }
  }

  // Tratar especificamente aulas canceladas com hora '00:00'
  if (hora === "00:00" && aula.status === "cancelada") {
    return {
      horaInicio: "Cancelada",
      horaFim: "Cancelada",
    };
  }

  // Com base no status da aula, interpretamos a hora
  switch (aula.status) {
    case "atual":
    case "em_andamento":
      // Para aulas em andamento, a hora é o horário de início
      return {
        horaInicio: hora,
        horaFim: "--:--", // Ainda não finalizada
      };
    case "realizada":
    case "finalizada":
      // Para aulas finalizadas, assumir duração de 1 hora
      try {
        const [horas, minutos] = hora.split(":").map(Number);
        const inicioDate = new Date();
        inicioDate.setHours(horas, minutos);

        // Subtrair 1 hora para obter horário de início estimado
        inicioDate.setHours(inicioDate.getHours() - 1);

        const horaInicioFormatada = inicioDate.toTimeString().slice(0, 5);

        return {
          horaInicio: horaInicioFormatada,
          horaFim: hora, // A hora registrada é de fim
        };
      } catch (error) {
        return {
          horaInicio: "Não registrado",
          horaFim: "Não registrado",
        };
      }
    default:
      return {
        horaInicio: hora,
        horaFim: "--:--",
      };
  }
};

const DetalheCadastroAluno = ({ aluno, alunoId, onNavigateBack }) => {
  // ?from=sala: veio do fluxo de aula (full screen/Sala) — "Voltar" leva
  // pra Sala, não pra lista de Alunos, e propaga a origem pro Editar.
  const [searchParams] = useSearchParams();
  const origemSala = searchParams.get("from") === "sala";
  // Estado para armazenar os dados do aluno quando recebemos apenas o ID
  const [dadosAluno, setDadosAluno] = useState(null);
  const [carregandoAluno, setCarregandoAluno] = useState(false);
  const [erro, setErro] = useState(null);
  const [editandoObservacoes, setEditandoObservacoes] = useState(false);
  const [observacoes, setObservacoes] = useState("");
  const [salvandoObservacoes, setSalvandoObservacoes] = useState(false);
  const [sucessoSalvar, setSucessoSalvar] = useState(false);
  const [historicoAulas, setHistoricoAulas] = useState([]); // Estado para histórico de aulas
  const [carregandoHistorico, setCarregandoHistorico] = useState(false); // Estado para carregamento

  // ── Faltas do aluno (pedido Rodrigo 14/07): tabela na ficha com as faltas
  // registradas; admin/líder justificam e desfazem — as regras (papéis e
  // prazo de justificativa até o fim do dia da aula, depois só admin) são
  // validadas pelo trigger do banco; aqui só mostramos o erro que ele der.
  const { user, isAdmin } = useAuth();
  const viewAsFaltas = isAdmin ? getViewAs(user?.email) : null;
  const papelEfetivo = viewAsFaltas?.role || user?.role;
  const podeEditarFaltas = ["admin", "lider"].includes(papelEfetivo);
  // Cargas são domínio do professor — recepção não mexe (pedido Rodrigo 22/07).
  const podeEditarCargas = papelEfetivo !== "recepcao";
  const [faltas, setFaltas] = useState([]);
  const [carregandoFaltas, setCarregandoFaltas] = useState(false);
  const [erroFaltas, setErroFaltas] = useState(null);
  // {id, texto} = modal aberto pra justificar/editar justificativa
  const [faltaModal, setFaltaModal] = useState(null);
  const [salvandoFalta, setSalvandoFalta] = useState(false);

  // Quando componente recebe alunoId em vez do objeto aluno completo
  useEffect(() => {
    const buscarDadosAluno = async () => {
      if (!alunoId) return;

      try {
        setCarregandoAluno(true);
        setErro(null);

        const { data, error } = await supabase
          .from("alunos")
          .select("*")
          .eq("id", alunoId)
          .single();

        if (error) {
          console.error("Erro ao buscar dados do aluno:", error);
          setErro(`Erro ao buscar dados do aluno: ${error.message}`);
          return;
        }

        if (!data) {
          setErro(`Aluno com ID ${alunoId} não encontrado`);
          return;
        }

        setDadosAluno(data);
        setObservacoes(data.observacoes || "");
      } catch (err) {
        console.error("Erro ao carregar dados do aluno:", err);
        setErro(`Erro ao carregar dados do aluno: ${err.message}`);
      } finally {
        setCarregandoAluno(false);
      }
    };

    if (alunoId && !aluno) {
      buscarDadosAluno();
    } else if (aluno) {
      setObservacoes(aluno.observacoes || "");
    }
  }, [alunoId, aluno]);

  // Usar o objeto aluno que foi passado diretamente, ou o que foi buscado pelo ID
  const alunoAtual = aluno || dadosAluno;

  // Efeito para carregar o histórico de aulas
  useEffect(() => {
    const carregarHistoricoAulas = async () => {
      if (!alunoAtual || !alunoAtual.id) return;

      try {
        setCarregandoHistorico(true);

        // Usar o método específico para buscar aulas por ID do aluno
        const aulasDoAluno = await aulasService.getAulasByAlunoId(
          alunoAtual.id
        );

        setHistoricoAulas(aulasDoAluno);
      } catch (err) {
        console.error("Erro ao buscar histórico de aulas:", err);
        setHistoricoAulas([]); // Garantir que é um array vazio em caso de erro
      } finally {
        setCarregandoHistorico(false);
      }
    };

    carregarHistoricoAulas();
  }, [alunoAtual]);

  // Carregar as faltas do aluno (aula_alunos.status='falta' + data/professor
  // da aula). Ordena da mais recente pra mais antiga.
  const carregarFaltas = useCallback(async () => {
    if (!alunoAtual?.id) return;
    try {
      setCarregandoFaltas(true);
      setErroFaltas(null);
      const { data, error } = await supabase
        .from("aula_alunos")
        .select(
          "id, justificativa, justificada_em, aulas(data, professores(nome))"
        )
        .eq("aluno_id", alunoAtual.id)
        .eq("status", "falta");
      if (error) throw error;
      const ordenadas = (data || []).sort((a, b) =>
        (b.aulas?.data || "").localeCompare(a.aulas?.data || "")
      );
      setFaltas(ordenadas);
    } catch (err) {
      console.error("Erro ao carregar faltas:", err);
      setErroFaltas("Erro ao carregar as faltas: " + err.message);
    } finally {
      setCarregandoFaltas(false);
    }
  }, [alunoAtual?.id]);

  useEffect(() => {
    carregarFaltas();
  }, [carregarFaltas]);

  // Salvar/editar justificativa — o trigger valida papel e prazo (até o fim
  // do dia da aula; depois só admin) e devolve mensagem clara se negar.
  const salvarJustificativaFalta = async () => {
    if (!faltaModal) return;
    try {
      setSalvandoFalta(true);
      setErroFaltas(null);
      const { error } = await supabase
        .from("aula_alunos")
        .update({
          justificativa: faltaModal.texto.trim() || null,
          justificada_por: user?.id || null,
          justificada_em: new Date().toISOString(),
        })
        .eq("id", faltaModal.id);
      if (error) throw error;
      setFaltaModal(null);
      await carregarFaltas();
    } catch (err) {
      console.error("Erro ao justificar falta:", err);
      setErroFaltas(err.message);
      setFaltaModal(null);
    } finally {
      setSalvandoFalta(false);
    }
  };

  // Desfazer falta: vira presença confirmada (consome o check-in, igual ao
  // fechamento de aula). Transição de 'falta' só passa pra admin/líder.
  const desfazerFalta = async (falta) => {
    const ok = window.confirm(
      `Desfazer a falta de ${formatarData(
        falta.aulas?.data
      )}? O aluno passa a contar como presente nessa aula.`
    );
    if (!ok) return;
    try {
      setSalvandoFalta(true);
      setErroFaltas(null);
      const { error } = await supabase
        .from("aula_alunos")
        .update({
          status: "presente",
          consumiu_checkin: true,
          confirmada_em: new Date().toISOString(),
          justificativa: null,
          justificada_por: null,
          justificada_em: null,
        })
        .eq("id", falta.id);
      if (error) throw error;
      await carregarFaltas();
    } catch (err) {
      console.error("Erro ao desfazer falta:", err);
      setErroFaltas(err.message);
    } finally {
      setSalvandoFalta(false);
    }
  };

  // Função para obter o rótulo de status
  // `statusAluno` é a presença DESTE aluno na aula (aula_alunos.status). Ela
  // manda no rótulo: uma aula finalizada em que o aluno faltou é "Falta" pra
  // ele, não "Realizada" (fix 27/07).
  const getStatusLabel = (status, aulaData, statusAluno) => {
    if (statusAluno === "falta") {
      return <span className="status-falta">Falta</span>;
    }

    // Verifica se a aula é antiga (data anterior à atual) mas ainda tem status "atual"
    if (status === "atual" || status === "em_andamento") {
      // Comparar apenas as datas no formato YYYY-MM-DD para evitar problemas de timezone
      const dataAulaString = aulaData.split("T")[0]; // Garantir formato YYYY-MM-DD
      const hoje = new Date();
      const hojeString =
        hoje.getFullYear() +
        "-" +
        String(hoje.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(hoje.getDate()).padStart(2, "0");

      // Se a data da aula for anterior à data atual, considerar como finalizada
      if (dataAulaString < hojeString) {
        return <span className="status-realizada">Finalizada</span>;
      }
    }

    // Tratamento normal para outros casos
    switch (status) {
      case "realizada":
      case "finalizada":
        // Aula finalizada com o aluno ainda 'pendente': o professor encerrou
        // sem marcar presença nem falta. Não é presença — não pode virar
        // "Realizada".
        if (statusAluno === "pendente") {
          return <span className="status-atual">Sem registro</span>;
        }
        return <span className="status-realizada">Realizada</span>;
      case "cancelada":
        return <span className="status-cancelada">Cancelada</span>;
      case "atual":
      case "em_andamento":
      default:
        return <span className="status-atual">Atual</span>;
    }
  };

  // Função para salvar as observações editadas
  const salvarObservacoes = async () => {
    if (!alunoAtual || !alunoAtual.id) return;

    try {
      setSalvandoObservacoes(true);
      setErro(null);
      setSucessoSalvar(false);

      const { error } = await supabase
        .from("alunos")
        .update({ observacoes })
        .eq("id", alunoAtual.id);

      if (error) throw error;

      // Atualizar o objeto local
      if (dadosAluno) {
        setDadosAluno({
          ...dadosAluno,
          observacoes,
        });
      }

      setSucessoSalvar(true);
      setEditandoObservacoes(false);

      // Esconder a mensagem de sucesso após alguns segundos
      setTimeout(() => {
        setSucessoSalvar(false);
      }, 3000);
    } catch (err) {
      console.error("Erro ao salvar observações:", err);
      setErro(`Erro ao salvar observações: ${err.message}`);
    } finally {
      setSalvandoObservacoes(false);
    }
  };

  // Função para formatar a data no padrão brasileiro
  const formatarData = (dataString) => {
    if (!dataString) return "Data não disponível";

    // Usar nossa função de formatação centralizada nas utils
    return formatarDataUtil(dataString);
  };

  const handleVoltar = () => {
    if (onNavigateBack) {
      onNavigateBack(origemSala ? "/sala" : "/alunos");
    }
  };
  const labelVoltar = origemSala
    ? "← Voltar para Sala"
    : "Voltar para Lista de Alunos";

  // Exibir mensagem de carregamento quando estiver buscando dados do aluno
  if (carregandoAluno) {
    return (
      <div className="loading-container">Carregando dados do aluno...</div>
    );
  }

  // Exibir mensagem de erro se ocorrer algum problema
  if (erro) {
    return (
      <div className="error-container">
        <p className="error-message">{erro}</p>
        <button className="btn-voltar" onClick={handleVoltar}>
          {labelVoltar}
        </button>
      </div>
    );
  }

  // Se não tem aluno nem dados do aluno, mostrar mensagem
  if (!alunoAtual) {
    return (
      <div className="error-container">
        <p>Aluno não encontrado</p>
        <button className="btn-voltar" onClick={handleVoltar}>
          {labelVoltar}
        </button>
      </div>
    );
  }

  const totalRealizadas = historicoAulas.filter(ehRealizada).length;
  const temRestricao =
    alunoAtual.lesao && alunoAtual.lesao !== "Nao" && alunoAtual.lesao !== "Não";

  return (
    <div className="detalhes-aluno">
      <div className="detalhe-hero">
        <div className="detalhe-hero-info">
          <span className="detalhe-av">{iniciais(alunoAtual.nome)}</span>
          <div>
            <h1>{alunoAtual.nome}</h1>
            <div className="detalhe-badges">
              {alunoAtual.plano && (
                <span className="badge-plano">{alunoAtual.plano}</span>
              )}
              {alunoAtual.nivel && (
                <span className="badge-iron">{alunoAtual.nivel}</span>
              )}
              <span
                className={`badge-iron ${
                  alunoAtual.status === "ativo" ? "" : "badge-inativo"
                }`}
              >
                {alunoAtual.status === "ativo" ? "Ativo" : "Inativo"}
              </span>
              {temRestricao && (
                <span className="badge-restricao">Restrição</span>
              )}
              <span className="badge-aulas">
                {totalRealizadas} aula{totalRealizadas === 1 ? "" : "s"}{" "}
                realizada{totalRealizadas === 1 ? "" : "s"}
              </span>
            </div>
          </div>
        </div>
        <div className="detalhe-hero-acoes">
          <button
            className="btn-editar"
            onClick={() =>
              onNavigateBack(
                `/editar-aluno/${alunoAtual.id}${origemSala ? "?from=sala" : ""}`
              )
            }
          >
            ✎ Editar Cadastro
          </button>
          {/* Tela separada do cadastro (reunião 12/06): carga máxima +
              volume por grupamento. Preview · Fase 3, ver CargasAluno.jsx.
              Escondido pra recepção (pedido Rodrigo 22/07): cargas são
              domínio do professor. */}
          {podeEditarCargas && (
            <button
              className="btn-editar btn-cargas"
              onClick={() => onNavigateBack(`/cargas/${alunoAtual.id}`)}
            >
              Editar Cargas
            </button>
          )}
          <button className="btn-voltar" onClick={handleVoltar}>
            {origemSala ? "← Voltar para Sala" : "← Voltar"}
          </button>
        </div>
      </div>

      {sucessoSalvar && (
        <div className="success-message">Treino salvo com sucesso!</div>
      )}

      <div className="detalhes-grid">
        {/* Dados Pessoais */}
        <div className="dados-aluno-card">
          <h3>Dados Pessoais</h3>
          <div className="info-container">
            <p>
              <strong>Nome Completo:</strong> {alunoAtual.nome}
            </p>
            <p>
              <strong>Telefone:</strong>{" "}
              {alunoAtual.telefone || "Não cadastrado"}
            </p>
            <p>
              <strong>Data de Nascimento:</strong>{" "}
              {alunoAtual.data_nascimento
                ? formatarData(alunoAtual.data_nascimento)
                : "Não cadastrada"}
            </p>
            <p>
              <strong>Idade:</strong> {alunoAtual.idade || "N/A"}
            </p>
            <p>
              <strong>Status da Matrícula:</strong>{" "}
              <span
                className={`badge-status ${
                  alunoAtual.status === "ativo"
                    ? "badge-status-ativo"
                    : "badge-status-inativo"
                }`}
              >
                {alunoAtual.status === "ativo" ? "Ativo" : "Inativo"}
              </span>
            </p>
          </div>
        </div>

        {/* Informações de Treino */}
        <div className="dados-aluno-card">
          <h3>Informações de Treino</h3>
          <div className="info-container">
            <p>
              <strong>Plano:</strong> {alunoAtual.plano || "N/A"}
            </p>
            <p>
              <strong>Nível:</strong> {alunoAtual.nivel || "N/A"}
            </p>

            <div className="lesao-info">
              <p>
                <strong>Lesão:</strong>{" "}
                <span
                  className={`${
                    alunoAtual.lesao?.includes("Grave")
                      ? "lesao-grave"
                      : alunoAtual.lesao?.includes("Moderada")
                      ? "lesao-moderada"
                      : ""
                  }`}
                >
                  {alunoAtual.lesao || "Nenhuma"}
                </span>
              </p>

              {alunoAtual.lesao &&
                alunoAtual.lesao !== "Nao" &&
                alunoAtual.tipo_lesao && (
                  <div className="tipo-lesao">
                    <p>
                      <strong>Detalhes da lesão:</strong>
                    </p>
                    <p className="lesao-descricao">{alunoAtual.tipo_lesao}</p>
                  </div>
                )}
            </div>

            {/* Rótulo VERDE e campo `objetivo` — igual ao full screen. Esta
                tela tinha ficado pra trás na reversão de 09/07: mostrava o
                objetivo sob o rótulo "Exercícios Obrigatórios" (bug pego pelo
                Rodrigo/Tales no go-live 22/07). O bloco de exercícios
                obrigatórios REAL fica logo após o Treino, como no full screen. */}
            <div className="objetivo-treino">
              <p className="objetivo-treino-titulo">
                <strong style={{ color: "var(--color-green, #99C42D)" }}>
                  Objetivo de Treino:
                </strong>
              </p>
              <div className="objetivo-treino-texto">
                {alunoAtual.objetivo || "Não informado"}
              </div>
            </div>

            <div className="observacoes-section">
              <p className="titulo-observacoes">
                <strong>Treino:</strong>
                {!editandoObservacoes && (
                  <button
                    className="btn-editar-mini"
                    onClick={() => setEditandoObservacoes(true)}
                  >
                    Editar
                  </button>
                )}
              </p>

              {editandoObservacoes ? (
                <div className="observacoes-editor">
                  <textarea
                    value={observacoes}
                    onChange={(e) => setObservacoes(e.target.value)}
                    rows={5}
                    placeholder="Digite observações sobre o aluno..."
                    className="observacoes-textarea"
                  />
                  <div className="observacoes-actions">
                    <button
                      className="btn-cancelar-mini"
                      onClick={() => {
                        setEditandoObservacoes(false);
                        setObservacoes(alunoAtual.observacoes || "");
                      }}
                      disabled={salvandoObservacoes}
                    >
                      Cancelar
                    </button>
                    <button
                      className="btn-salvar-observacao"
                      onClick={salvarObservacoes}
                      disabled={salvandoObservacoes}
                    >
                      {salvandoObservacoes ? "Salvando..." : "Salvar"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="observacoes-texto">
                  {observacoes || "Nenhuma observação cadastrada"}
                </div>
              )}
            </div>

            {/* Exercícios obrigatórios REAIS (alunos.exercicios_obrigatorios)
                — mesma ordem do full screen: Objetivo → Treino → Obrigatórios. */}
            <div className="objetivo-treino">
              <p className="objetivo-treino-titulo">
                <strong style={{ color: "var(--color-error, #EF4444)" }}>
                  Exercícios Obrigatórios:
                </strong>
              </p>
              <div className="objetivo-treino-texto">
                {alunoAtual.exercicios_obrigatorios?.trim() ||
                  "Nenhum exercício obrigatório registrado."}
              </div>
            </div>
          </div>
        </div>

        {/* Faltas (pedido 14/07): tabela com as faltas do aluno; admin e
            líder justificam/desfazem — professor e recepção só visualizam. */}
        <div className="dados-aluno-card faltas-aluno">
          <h3>
            Faltas
            <span className="faltas-total">{faltas.length}</span>
          </h3>
          <div className="info-container">
            {erroFaltas && <div className="faltas-erro">{erroFaltas}</div>}
            {carregandoFaltas ? (
              <p>Carregando faltas...</p>
            ) : faltas.length === 0 ? (
              <div className="sem-registros">
                Nenhuma falta registrada para este aluno.
              </div>
            ) : (
              <div className="faltas-tabela-wrap">
                <table className="faltas-tabela">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Professor</th>
                      <th>Justificativa</th>
                      {podeEditarFaltas && <th className="faltas-acoes">Ações</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {faltas.map((f) => (
                      <tr key={f.id}>
                        <td>{formatarData(f.aulas?.data)}</td>
                        <td>{f.aulas?.professores?.nome || "—"}</td>
                        <td className="faltas-just">
                          {f.justificativa ? (
                            <>
                              {f.justificativa}
                              {f.justificada_em && (
                                <span className="faltas-just-quando">
                                  {" "}
                                  · em {formatarData(f.justificada_em)}
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="faltas-sem-just">
                              Não justificada
                            </span>
                          )}
                        </td>
                        {podeEditarFaltas && (
                          <td className="faltas-acoes">
                            <button
                              className="btn-falta-acao"
                              disabled={salvandoFalta}
                              onClick={() =>
                                setFaltaModal({
                                  id: f.id,
                                  texto: f.justificativa || "",
                                })
                              }
                            >
                              {f.justificativa ? "Editar just." : "Justificar"}
                            </button>
                            <button
                              className="btn-falta-acao btn-falta-desfazer"
                              disabled={salvandoFalta}
                              onClick={() => desfazerFalta(f)}
                            >
                              Desfazer
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Histórico de Aulas */}
        <div className="dados-aluno-card historico-aulas">
          <h3>Histórico de Aulas</h3>
          <div className="info-container">
            {carregandoHistorico ? (
              <div className="carregando-historico">
                <div className="spinner"></div>
                <p>Carregando histórico de aulas...</p>
              </div>
            ) : historicoAulas.length > 0 ? (
              <div className="historico-aulas-lista">
                {historicoAulas.map((aula) => (
                  <div
                    key={aula.id}
                    className={`aula-card aula-${aula.status}`}
                  >
                    <div className="aula-header">
                      <div className="aula-data-horario">
                        <div className="aula-data">
                          {formatarData(aula.data)}
                        </div>
                        <div className="aula-horario">
                          {(() => {
                            const horarios = interpretarHorarios(aula);
                            if (
                              aula.status === "realizada" ||
                              aula.status === "finalizada"
                            ) {
                              return `${horarios.horaInicio} - ${horarios.horaFim}`;
                            }
                            return horarios.horaInicio || "--:--";
                          })()}
                        </div>
                      </div>
                      <div className="aula-professor">
                        Professor:{" "}
                        {aula.professor ? aula.professor.nome : "Não definido"}
                      </div>
                      <div className="aula-status">
                        Status:{" "}
                        {getStatusLabel(
                          aula.status,
                          aula.data,
                          aula.status_aluno
                        )}
                      </div>
                    </div>
                    <div className="aula-detalhes">
                      <div className="aula-info-detalhada">
                        <h4>Informações</h4>
                        <p>
                          <strong>Data:</strong> {formatarData(aula.data)}
                        </p>
                        <div className="horario-container">
                          {(() => {
                            const horarios = interpretarHorarios(aula);

                            if (
                              aula.status === "finalizada" ||
                              aula.status === "realizada"
                            ) {
                              return (
                                <>
                                  <div className="horario-inicio">
                                    <strong>Horário de Início:</strong>{" "}
                                    <span className="hora-valor">
                                      {horarios.horaInicio}
                                    </span>
                                  </div>
                                  <div className="horario-fim">
                                    <strong>Horário de Término:</strong>{" "}
                                    <span className="hora-valor">
                                      {horarios.horaFim}
                                    </span>
                                  </div>
                                </>
                              );
                            } else {
                              return (
                                <>
                                  <div className="horario-inicio">
                                    <strong>Horário de Início:</strong>{" "}
                                    <span className="hora-valor">
                                      {horarios.horaInicio}
                                    </span>
                                  </div>
                                  <div className="horario-fim">
                                    <strong>Horário de Término:</strong>{" "}
                                    <span className="hora-valor">
                                      {horarios.horaFim}
                                    </span>
                                  </div>
                                </>
                              );
                            }
                          })()}
                        </div>
                        <p>
                          <strong>Professor:</strong>{" "}
                          {aula.professor
                            ? aula.professor.nome
                            : "Não definido"}
                        </p>
                      </div>
                      <div className="aula-exercicios">
                        <h4>Exercícios</h4>
                        {aula.exercicios && aula.exercicios.length > 0 ? (
                          <ul>
                            {aula.exercicios.map((exercicio, index) => (
                              <li key={exercicio.id || index}>
                                {exercicio.nome}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p>Nenhum exercício registrado.</p>
                        )}
                      </div>
                      <div className="aula-notas">
                        <h4>Anotações</h4>
                        <p>{aula.observacoes || "Nenhuma anotação."}</p>
                      </div>
                      {aula.lesoes && (
                        <div className="aula-lesoes">
                          <h4>Lesões/Restrições</h4>
                          <p>{aula.lesoes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="sem-registros">
                Nenhuma aula registrada para este aluno.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de justificativa (mesmo visual do modal da Sala — as classes
          justificativa-* são globais via Sala.css). */}
      {faltaModal && (
        <div
          className="justificativa-overlay"
          onClick={() => !salvandoFalta && setFaltaModal(null)}
        >
          <div
            className="justificativa-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Justificar falta — {alunoAtual.nome}</h3>
            <textarea
              className="justificativa-textarea"
              rows={5}
              value={faltaModal.texto}
              onChange={(e) =>
                setFaltaModal((prev) => ({ ...prev, texto: e.target.value }))
              }
              placeholder="Descreva o motivo da falta..."
            />
            <div className="justificativa-actions">
              <button
                className="btn-cancelar-aluno"
                onClick={() => setFaltaModal(null)}
                disabled={salvandoFalta}
              >
                Cancelar
              </button>
              <button
                className="btn-finalizar-aluno"
                onClick={salvarJustificativaFalta}
                disabled={salvandoFalta}
              >
                {salvandoFalta ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DetalheCadastroAluno;
